/**
 * 公用设施网络的**共享拓扑层**（G010 从 G009 的 power.ts 抽出）。
 *
 * 电力、氧气、水的差别全在结算：电力瞬时平衡、流体可储存。但「导管铺在格子上、
 * 建筑靠相邻接入、连通分量各自独立结算」这一层与传输的是什么完全无关，
 * 因此抽到这里，由各域复用。
 *
 * 抽取时必须原样保留 G009 被对抗验证打穿过的那个性质：
 * **缓存按状态对象分键（WeakMap），不是模块级单例**。模块单例会跨内核实例串味，
 * 因为 entityId 是每内核独立的计数器，两个内核必然产出同一串 ID —— 后果不止
 * 投影出错，stateHash() 会因「另一个内核是否存在」而改变。
 */

import { cellKey, type BuildCellId, type SpatialMapData } from '../spatial/types.js';
import { neighbors } from '../spatial/ops.js';
import { BUILDING_COMPONENT, type BuildingComponent } from '../building/plan-building.js';

/* ===================== 并查集 ===================== */

/**
 * 并查集。**按键的字典序选根**，不按 rank/size —— 牺牲一点效率换取
 * 「代表元与 union 顺序无关」。分量划分本身与顺序无关，但代表元不是，
 * 而代表元会成为对外可见的分量 ID。
 */
export class DeterministicUnionFind {
  private readonly parent = new Map<string, string>();

  add(key: string): void {
    if (!this.parent.has(key)) this.parent.set(key, key);
  }

  find(key: string): string {
    let root = key;
    while (this.parent.get(root) !== root) {
      const next = this.parent.get(root);
      if (next === undefined) { this.parent.set(root, root); return root; }
      root = next;
    }
    // 路径压缩（只改父指针，不影响最终划分）
    let cur = key;
    while (cur !== root) {
      const next = this.parent.get(cur)!;
      this.parent.set(cur, root);
      cur = next;
    }
    return root;
  }

  union(a: string, b: string): void {
    const ra = this.find(a), rb = this.find(b);
    if (ra === rb) return;
    if (ra < rb) this.parent.set(rb, ra);
    else this.parent.set(ra, rb);
  }
}

/* ===================== 拓扑 ===================== */

/** 一个连通分量（不含任何随 Tick 变化的数值）。 */
export interface NetworkComponent {
  /** 分量 ID = 分量内字典序最小的 cellKey，稳定且与顺序无关。 */
  readonly id: string;
  /** 属于本分量的导管格（电缆 / 管道）。 */
  readonly conduitKeys: readonly string[];
  /** 接入本分量的建筑实体 ID（已排序）。 */
  readonly entityIds: readonly string[];
}

export interface NetworkTopology {
  readonly components: readonly NetworkComponent[];
  /** entityId -> 分量 ID；未接入的实体不在表中。 */
  readonly componentOfEntity: Readonly<Record<string, string>>;
}

interface CachedTopology extends NetworkTopology {
  version: number;
  mapCellCount: number;
  /**
   * 上次建拓扑时「运转中的建筑」的确定序列表。
   *
   * 导管的增删由各域的命令负责版本号 +1，但**建筑的增删与生命周期转换同样
   * 改变拓扑**，而领域模块之间不能互相 import（循环依赖）。靠它们记得置脏是
   * 最容易漏的那类 bug，因此这里逐项比对：走 entities.order 的 O(n) 遍历，
   * 145 实体约 1 µs，精确且免疫「忘了置脏」。
   */
  operationalIds: string[];
}

export interface EntityTableLike {
  order: readonly string[];
  components: Record<string, Record<string, unknown>>;
}

/** 采集当前「运转中」的建筑 ID（确定序）。 */
export function operationalBuildingIds(entities: EntityTableLike): string[] {
  const bucket = entities.components[BUILDING_COMPONENT];
  if (!bucket) return [];
  const out: string[] = [];
  for (const id of entities.order) {
    const c = bucket[id] as unknown as BuildingComponent | undefined;
    if (c && c.lifecycle === 'operational') out.push(id);
  }
  return out;
}

/** 读取全部运转中的建筑组件（确定序）。 */
export function operationalBuildings(
  entities: EntityTableLike,
): Array<{ entityId: string; building: BuildingComponent }> {
  const bucket = entities.components[BUILDING_COMPONENT];
  if (!bucket) return [];
  const out: Array<{ entityId: string; building: BuildingComponent }> = [];
  for (const id of entities.order) {
    const c = bucket[id] as unknown as BuildingComponent | undefined;
    if (c && c.lifecycle === 'operational') out.push({ entityId: id, building: c });
  }
  return out;
}

function sameIds(a: readonly string[], b: readonly string[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
  return true;
}

/**
 * 重建拓扑。
 *
 * 建筑既是节点也是**桥**：一栋建筑若同时贴着两个原本不连通的导管分量，
 * 它会把两者连成一个 —— 物理上正确，也避免了「贴着多个分量时挑哪个」这种
 * 任意选择。并查集最终划分与 union 顺序无关，故此处无需排序。
 */
function rebuild(
  map: SpatialMapData,
  conduitCells: Readonly<Record<string, true>>,
  entities: EntityTableLike,
  version: number,
): CachedTopology {
  const uf = new DeterministicUnionFind();
  const conduitKeys = Object.keys(conduitCells).sort();
  for (const k of conduitKeys) uf.add(k);

  for (const k of conduitKeys) {
    const [layer, ring, sector] = k.split(':');
    const id: BuildCellId = {
      layer: layer as BuildCellId['layer'], ring: Number(ring), sector: Number(sector),
    };
    for (const nb of neighbors(map, id)) {
      if (conduitCells[cellKey(nb)]) uf.union(k, cellKey(nb));
    }
  }

  const componentOfEntity: Record<string, string> = {};
  const attachments: Array<{ entityId: string; roots: string[] }> = [];
  for (const { entityId, building } of operationalBuildings(entities)) {
    const touched = new Set<string>();
    for (const c of building.cells) {
      const ck = cellKey(c);
      if (conduitCells[ck]) touched.add(ck);
      for (const nb of neighbors(map, c)) {
        const nk = cellKey(nb);
        if (conduitCells[nk]) touched.add(nk);
      }
    }
    if (touched.size === 0) continue;
    attachments.push({ entityId, roots: [...touched].sort() });
  }
  for (const att of attachments) {
    for (let i = 1; i < att.roots.length; i++) uf.union(att.roots[0]!, att.roots[i]!);
  }

  const byRoot = new Map<string, { conduitKeys: string[]; entityIds: string[] }>();
  for (const k of conduitKeys) {
    const r = uf.find(k);
    let e = byRoot.get(r);
    if (!e) { e = { conduitKeys: [], entityIds: [] }; byRoot.set(r, e); }
    e.conduitKeys.push(k);
  }
  for (const att of attachments) {
    const r = uf.find(att.roots[0]!);
    componentOfEntity[att.entityId] = r;
    byRoot.get(r)?.entityIds.push(att.entityId);
  }

  const components: NetworkComponent[] = [...byRoot.entries()]
    .map(([id, v]) => ({
      id,
      conduitKeys: v.conduitKeys.slice().sort(),
      entityIds: v.entityIds.slice().sort(),
    }))
    .sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));

  return {
    version,
    mapCellCount: Object.keys(map.cells).length,
    operationalIds: operationalBuildingIds(entities),
    components,
    componentOfEntity,
  };
}

/**
 * 拓扑缓存：**按持有导管的状态对象分键**，不进 WorldState。
 *
 * 曾经是模块级单例，被对抗验证打穿（见 G009 报告 4.2）。WeakMap 以状态对象
 * 本身为键，天然一实例一缓存；fromSnapshot 走 structuredClone 产出新对象，
 * 也自然不会复用旧缓存。
 */
const caches = new WeakMap<object, CachedTopology>();

/** 测试用：按实例清空拓扑缓存。 */
export function clearNetworkCache(owner: object): void {
  caches.delete(owner);
}

/**
 * 取（必要时重建）某个网络的拓扑。
 *
 * @param owner 持有该网络的状态对象（作为缓存键，一般就是域状态本身）
 * @param version 拓扑版本号，由域的命令在改变导管集合时递增
 */
export function networkTopology(
  owner: object,
  map: SpatialMapData,
  conduitCells: Readonly<Record<string, true>>,
  entities: EntityTableLike,
  version: number,
): NetworkTopology {
  const cached = caches.get(owner);
  if (cached && cached.version === version
    && cached.mapCellCount === Object.keys(map.cells).length
    && sameIds(cached.operationalIds, operationalBuildingIds(entities))) {
    return cached;
  }
  const built = rebuild(map, conduitCells, entities, version);
  caches.set(owner, built);
  return built;
}
